import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatError } from "../lib/auth";
import { compressImage } from "../lib/imageCompress";
import { Heart, ArrowLeft, Camera } from "lucide-react";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    email: "", password: "", name: "", age: 25, gender: "female",
    looking_for: "male", bio: "", photo_url: "",
    terms_accepted: false, marketing_opt_in: false,
  });
  const [photoPreview, setPhotoPreview] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handlePhoto = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError("Fotka je moc velká (max 25 MB).");
      e.target.value = "";
      return;
    }
    try {
      const compressed = await compressImage(file);
      setPhotoPreview(compressed);
      setForm((f) => ({ ...f, photo_url: compressed }));
    } catch (err) {
      setError(err.message || "Nepodařilo se zpracovat fotku.");
    } finally {
      e.target.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.terms_accepted) {
      setError("Pro registraci musíš souhlasit s podmínkami a ochranou osobních údajů.");
      return;
    }
    if (parseInt(form.age, 10) < 18) {
      setError("Pro použití Sentra musíš být starší 18 let.");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, age: parseInt(form.age, 10) });
      nav("/questionnaire");
    } catch (err) {
      setError(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="app-shell bg-aurora min-h-screen px-7 pt-12 pb-10" data-testid="register-page">
      <Link to="/" className="inline-flex items-center gap-1 text-[#8A7E91] mb-6" data-testid="back-landing-reg">
        <ArrowLeft size={18} strokeWidth={1.7} /> <span className="text-sm">Zpět</span>
      </Link>

      <div className="flex items-center gap-2 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#082A24] to-[#0F3D34] flex items-center justify-center shadow-lg">
          <Heart size={20} fill="white" strokeWidth={0} className="text-white" />
        </div>
        <span className="font-display text-2xl font-semibold text-[#332D38]">Sentra</span>
      </div>

      <h1 className="font-display text-4xl text-[#332D38] mb-2">Pojďme začít ✨</h1>
      <p className="text-[#54635F] mb-6">Pár základních údajů a AI najde tvé spojení.</p>

      <form onSubmit={onSubmit} className="space-y-3" noValidate>
        {/* Photo upload */}
        <div className="flex justify-center mb-2">
          <button type="button" onClick={() => fileRef.current?.click()} className="relative w-24 h-24 rounded-full overflow-hidden bg-[#FFE4CC] border-2 border-dashed border-[#FF7A00] flex items-center justify-center" data-testid="reg-photo-upload">
            {photoPreview ? (
              <img src={photoPreview} alt="" className="w-full h-full object-cover" />
            ) : (
              <Camera size={26} className="text-[#FF7A00]" />
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
        </div>

        <div>
          <label className="label-soft">Jméno</label>
          <input required value={form.name} onChange={set("name")} className="input-field" placeholder="Tvé křestní jméno" data-testid="reg-name" />
        </div>
        <div>
          <label className="label-soft">E-mail</label>
          <input type="email" required value={form.email} onChange={set("email")} className="input-field" placeholder="ty@email.cz" data-testid="reg-email" />
        </div>
        <div>
          <label className="label-soft">Heslo (min. 6 znaků)</label>
          <input type="password" required minLength={6} value={form.password} onChange={set("password")} className="input-field" placeholder="••••••••" data-testid="reg-password" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-soft">Věk (18+)</label>
            <input type="number" min="18" max="99" required value={form.age} onChange={set("age")} className="input-field" data-testid="reg-age" />
          </div>
          <div>
            <label className="label-soft">Pohlaví</label>
            <select value={form.gender} onChange={set("gender")} className="input-field" data-testid="reg-gender">
              <option value="female">Žena</option>
              <option value="male">Muž</option>
              <option value="other">Jiné</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label-soft">Hledám</label>
          <select value={form.looking_for} onChange={set("looking_for")} className="input-field" data-testid="reg-looking">
            <option value="male">Muže</option>
            <option value="female">Ženu</option>
            <option value="any">Kohokoliv</option>
          </select>
        </div>
        <div>
          <label className="label-soft">Bio (krátký popis)</label>
          <textarea rows={3} value={form.bio} onChange={set("bio")} className="input-field" placeholder="Napiš pár vět o sobě..." data-testid="reg-bio" />
        </div>

        {/* Legal consent — REQUIRED by Apple/Google for dating apps */}
        <label className="flex items-start gap-3 p-3 rounded-2xl bg-white/70 border border-[#DEE7E2]">
          <input type="checkbox" required checked={form.terms_accepted} onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })} className="mt-1 w-5 h-5 accent-[#FF7A00]" data-testid="reg-terms" />
          <span className="text-xs text-[#332D38] leading-relaxed">
            Je mi <strong>18 nebo více let</strong> a souhlasím s{" "}
            <Link to="/terms" target="_blank" className="underline text-[#0F3D34] font-medium">Podmínkami použití</Link>{" "}
            a{" "}
            <Link to="/privacy" target="_blank" className="underline text-[#0F3D34] font-medium">Ochranou osobních údajů</Link>.
          </span>
        </label>

        <label className="flex items-start gap-3 p-3 rounded-2xl bg-white/50 border border-[#DEE7E2]">
          <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => setForm({ ...form, marketing_opt_in: e.target.checked })} className="mt-1 w-5 h-5 accent-[#FF7A00]" data-testid="reg-marketing" />
          <span className="text-xs text-[#54635F] leading-relaxed">Chci občas dostávat tipy a novinky (nepovinné, kdykoli lze zrušit).</span>
        </label>

        {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-2xl p-3" data-testid="reg-error">{error}</div>}

        <button type="submit" disabled={loading} className="btn-primary mt-4" data-testid="reg-submit">
          {loading ? "Vytvářím účet..." : "Vytvořit účet"}
        </button>
      </form>

      <p className="text-center text-sm text-[#8A7E91] mt-6">
        Už máš účet?{" "}
        <Link to="/login" className="text-[#C9A66B] font-medium" data-testid="go-login">Přihlas se</Link>
      </p>
    </div>
  );
}
